import { canonicalPrinting } from "@/utils/printingIdentity";
import { ResponsiveModal } from "@/components/ResponsiveModal/ResponsiveModal";
import { Tables } from "database.types";
import { useBinders } from "@/hooks/useBinders";
import { useCallback, useEffect, useMemo, useState } from "react";
import useVariantsCards from "@/hooks/useVariantsCards";
import { sortVariantsByConditions } from "@/utils/sortVariantsByConditions";
import { useGradingCompanies } from "@/hooks/useGradingCompanies";
import { sortGraders } from "@/utils/sortGraders";
import { useGradesQuery } from "@/queries/useGradesQuery";
import { sortGrades } from "@/utils/sortGrades";
import { Label } from "@radix-ui/react-label";
import { Input } from "@/components/ui/input";
import { usePriceInput } from "@/utils/usePriceInput";
import { formatPrice } from "@/utils/formatPrice";
import { Button } from "@/components/ui/button";
import { useGradedPricesStore } from "@/stores/useGradedPricesStore";
import { Spinner } from "@/components/ui/spinner";
import { AddCardToBinderBindersInput } from "../AddCardToBinderBindersInput/AddCardToBinderBindersInput";
import { AddCardToBinderOptions } from "../AddCardToBinderOptions/AddCardToBinderOptions";
import { getCardById } from "@/api/cards";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { updateVariantInBinder } from "@/api/binders";
import { BinderVariant, UpdateVariantInBinderOptions } from "@/types/binders";
import { X } from "lucide-react";
import { AddCardToBinderTotalPrice } from "../AddCardToBinderTotalPrice/AddCardToBinderTotalPrice";
import { EditCardInBinderDialogSkeleton } from "./EditCardInBinderDialogSkeleton";
import { invalidateBinderWatchlistVariantsQueries } from "@/queries/common";
import { binderEditPricingEligible } from "@/utils/binderDisplay";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  binderItem: BinderVariant;
  languageConfirmationMode?: boolean;
  onLanguageConfirmed?: () => void;
}

export function EditCardInBinderDialog({
  binderItem,
  open,
  onOpenChange,
  languageConfirmationMode = false,
  onLanguageConfirmed,
}: Props) {
  const { load: loadGradedPrice, prices: gradedPrices } =
    useGradedPricesStore();

  const cardId = binderItem.variant.card_id;

  const [isShowAddedAnimation, setIsShowAddedAnimation] = useState(false);

  const [wasSuccess, setWasSuccess] = useState(false);

  const [validationErrorMessage, setValidationErrorErrorMessage] = useState<
    string | null
  >(null);

  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (options: UpdateVariantInBinderOptions) => {
      return updateVariantInBinder(options);
    },
    onSuccess() {
      onSuccess();
      invalidateBinderWatchlistVariantsQueries();
      onLanguageConfirmed?.();
    },
  });

  const {
    data: catalogResponse,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
  } = useQuery({
    queryKey: ["card", cardId],
    queryFn: () => getCardById(cardId),
    enabled: open,
  });
  const catalogLanguage = catalogResponse?.data.language;
  const catalogLanguageLocked = ["tcgdex", "ximilar"].includes(
    catalogResponse?.data.catalog_source ?? "",
  );

  const { data: binders = [], isLoading: isLoadingBinders } = useBinders();

  const { data: variants = [], isLoading: isLoadingVariants } =
    useVariantsCards({
      cardId,
      enabled: open,
    });

  const { data: gradingCompanies, isLoading: isLoadingGradingCompanies } =
    useGradingCompanies();

  const { data: grades, isLoading: isLoadingGrades } = useGradesQuery();

  const [selectedVariantId, setSelectedVariantId] = useState<
    Tables<"variants">["id"] | null
  >(null);

  const [purchasePriceInput, setPurchasePriceInput] = usePriceInput("", {
    min: 0.01,
  });

  const [selectedBinderId, setSelectedBinderId] = useState<
    Tables<"binders">["id"] | null
  >(null);

  const [selectedLanguage, setSelectedLanguage] = useState<
    "english" | "japanese" | "chinese"
  >(binderItem.language ?? "english");
  const pricingEligible = binderEditPricingEligible(
    binderItem,
    selectedLanguage,
  );

  useEffect(() => {
    if (!open) return;
    const language = catalogLanguageLocked
      ? catalogLanguage
      : binderItem.language;
    if (
      language === "english" ||
      language === "japanese" ||
      language === "chinese"
    ) {
      setSelectedLanguage(language);
    }
  }, [
    open,
    binderItem.id,
    binderItem.language,
    catalogLanguage,
    catalogLanguageLocked,
  ]);

  const languageOptions = [
    { id: "english", label: "English" },
    { id: "japanese", label: "Japanese" },
    { id: "chinese", label: "Chinese" },
  ];

  const [selectedGradingCompanyId, setSelectedGradingCompanyId] = useState<
    Tables<"grading_companies">["id"] | null
  >(null);

  const [selectedGradeId, setSelectedGradeId] = useState<
    Tables<"grades">["id"] | null
  >(null);

  const foreignCatalog = ["japanese", "chinese"].includes(
    catalogLanguage ?? "",
  );
  const [physicalPrinting, setPhysicalPrinting] = useState<string | null>(null);
  const physicalPrintingOptions = [
    { id: "Normal", label: "Normal / Non-foil" },
    { id: "Holofoil", label: "Holofoil" },
    { id: "Reverse Holofoil", label: "Reverse Holofoil" },
    { id: "Poke Ball Holofoil", label: "Poké Ball Holofoil" },
    { id: "Master Ball Holofoil", label: "Master Ball Holofoil" },
    { id: "Unspecified", label: "Unknown — save without a price" },
  ];

  const selectedVariant = useMemo(() => {
    return variants.find((variant) => variant.id === selectedVariantId);
  }, [selectedVariantId, variants]);

  useEffect(() => {
    setPhysicalPrinting(canonicalPrinting(selectedVariant?.printing?.name));
  }, [selectedVariantId, selectedVariant?.printing?.name]);

  const gradingCompanyOptions = useMemo(() => {
    const sortedGradingCompanies = sortGraders(gradingCompanies?.data || []);

    return sortedGradingCompanies.map((gradingCompany) => ({
      id: gradingCompany.id,
      label: gradingCompany.name,
    }));
  }, [gradingCompanies]);

  const variantOptions = useMemo(() => {
    const sortedVariants = sortVariantsByConditions(variants);

    return sortedVariants.map((variant) => {
      const name = variant.condition.name;
      const price = variant.price;
      const formattedPrice =
        pricingEligible &&
        !selectedGradingCompanyId &&
        !selectedGradeId &&
        price
          ? formatPrice(price)
          : "";

      const description = variant.printing?.name
        ? `${name} · ${variant.printing.name}`
        : name;
      const label = formattedPrice
        ? `${description} (${formattedPrice})`
        : description;

      return {
        id: variant.id,
        label,
      };
    });
  }, [variants, pricingEligible, selectedGradingCompanyId, selectedGradeId]);

  const gradeOptions = useMemo(() => {
    const sortedGrades = sortGrades(grades || []);

    if (!sortedGrades) {
      return [];
    }

    return sortedGrades.map((grade) => ({
      id: grade.id,
      label: grade.name,
    }));
  }, [grades]);

  const sortedBinders = useMemo(() => {
    return [...binders].sort((a, b) => {
      return Number(b.card_count) - Number(a.card_count);
    });
  }, [binders]);

  useEffect(() => {
    if (variantOptions.length > 0 && !selectedVariantId) {
      setSelectedVariantId(variantOptions[0].id);
    }

    if (sortedBinders.length > 0 && !selectedBinderId) {
      setSelectedBinderId(sortedBinders[0].id);
    }
  }, [selectedBinderId, selectedVariantId, sortedBinders, variantOptions]);

  useEffect(() => {
    if (
      open &&
      pricingEligible &&
      selectedGradeId &&
      selectedGradingCompanyId
    ) {
      loadGradedPrice(cardId, selectedGradingCompanyId, selectedGradeId);
    }
  }, [
    open,
    pricingEligible,
    selectedGradeId,
    selectedGradingCompanyId,
    cardId,
    loadGradedPrice,
  ]);

  const isLoading =
    isLoadingCatalog ||
    isLoadingBinders ||
    isLoadingVariants ||
    isLoadingGradingCompanies ||
    isLoadingGrades;

  const isDisabled = isPending || isShowAddedAnimation || isCatalogError;

  const onSuccess = useCallback(() => {
    setWasSuccess(true);
    setIsShowAddedAnimation(true);
    setTimeout(() => {
      setIsShowAddedAnimation(false);
    }, 2500);
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (binderItem.grading_company_id) {
      setSelectedGradingCompanyId(binderItem.grading_company_id);
    }
    if (binderItem.grade_id) {
      setSelectedGradeId(binderItem.grade_id.toString());
    }

    if (binderItem.variant_id) {
      setSelectedVariantId(binderItem.variant_id);
    }

    if (binderItem.purchase_price) {
      setPurchasePriceInput(binderItem.purchase_price.toString());
    }

    if (binderItem.binder_id) {
      setSelectedBinderId(binderItem.binder_id);
    }
  }, [
    binderItem.grading_company_id,
    binderItem.grade_id,
    binderItem.variant_id,
    binderItem.purchase_price,
    setPurchasePriceInput,
    binderItem.binder_id,
  ]);

  const onSubmit = useCallback(async () => {
    setValidationErrorErrorMessage(null);

    if (!selectedBinderId || !selectedVariantId) {
      setValidationErrorErrorMessage(
        "Select the card's condition and printing before saving.",
      );
      return;
    }
    if (foreignCatalog && !physicalPrinting) {
      setValidationErrorErrorMessage(
        "Confirm the physical printing, or choose Unknown to save without a price.",
      );
      return;
    }

    if (selectedGradeId && !selectedGradingCompanyId) {
      setValidationErrorErrorMessage("Please select a grader");
      return;
    }

    if (selectedGradingCompanyId && !selectedGradeId) {
      setValidationErrorErrorMessage("Please select a grade");
      return;
    }

    const data = {
      binderId: selectedBinderId,
      variantId: selectedVariantId,
      printing: foreignCatalog ? (physicalPrinting ?? undefined) : undefined,
      purchasePrice: purchasePriceInput ? Number(purchasePriceInput) : null,
      language: selectedLanguage,
      gradingCompanyId: selectedGradingCompanyId,
      gradeId: selectedGradeId,
      scanned: false,
      relationId: binderItem.id,
    };

    await mutateAsync(data);
  }, [
    mutateAsync,
    purchasePriceInput,
    selectedBinderId,
    selectedLanguage,
    selectedGradeId,
    selectedGradingCompanyId,
    selectedVariantId,
    foreignCatalog,
    physicalPrinting,
    binderItem.id,
  ]);

  const onClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (isLoading) {
    return (
      <ResponsiveModal
        open={open}
        onOpenChange={onOpenChange}
        title={
          languageConfirmationMode
            ? "Confirm card language"
            : "Edit binder card"
        }
      >
        <EditCardInBinderDialogSkeleton />
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={
        languageConfirmationMode ? "Confirm card language" : "Edit binder card"
      }
      repositionInputs={false}
    >
      <div className="grid gap-4">
        {languageConfirmationMode && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
            <p className="font-bold">Confirm this card in three steps</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5">
              <li>Look at the printed text on your physical card.</li>
              <li>Select English, Japanese, or Chinese below.</li>
              <li>Press Confirm language to save it and update pricing.</li>
            </ol>
            <p className="mt-2 text-xs">
              You do not need to write a description. If you are unsure, close
              this window and scan the card instead.
            </p>
          </div>
        )}
        <AddCardToBinderOptions
          options={variantOptions}
          value={selectedVariantId}
          onChange={setSelectedVariantId}
          label="Variant"
          cancelable={false}
          disabled={isDisabled}
        />

        {foreignCatalog && (
          <div className="space-y-2">
            <AddCardToBinderOptions
              options={physicalPrintingOptions}
              value={physicalPrinting}
              onChange={setPhysicalPrinting}
              label="Physical printing"
              cancelable={false}
              disabled={isDisabled}
            />
            <p className="text-xs text-muted-foreground">
              Check the finish on your card. Art rarity does not confirm its
              foil pattern. Unknown printing can be saved, but its price will
              remain unavailable.
            </p>
          </div>
        )}

        <AddCardToBinderOptions
          options={languageOptions}
          value={selectedLanguage}
          onChange={(value) =>
            setSelectedLanguage(value as "english" | "japanese" | "chinese")
          }
          label="Card language"
          cancelable={false}
          disabled={isDisabled || catalogLanguageLocked}
        />

        <AddCardToBinderOptions
          options={gradingCompanyOptions}
          value={selectedGradingCompanyId}
          onChange={setSelectedGradingCompanyId}
          label="Grader"
          cancelable
          disabled={isDisabled}
        />

        <AddCardToBinderOptions
          options={gradeOptions}
          value={selectedGradeId}
          onChange={setSelectedGradeId}
          label="Grade"
          cancelable
          disabled={isDisabled}
        />
        <div className="grid gap-2">
          <Label className="font-medium">Purchase Price ($, optional)</Label>
          <Input
            placeholder=""
            value={purchasePriceInput}
            onChange={(e) => setPurchasePriceInput(e.target.value)}
            disabled={isDisabled}
          />
        </div>
        {cardId && selectedVariant && (
          <AddCardToBinderTotalPrice
            cardId={cardId}
            chineseScript={binderItem.scan_identity?.chineseScript ?? undefined}
            labelTier={binderItem.scan_identity?.slab?.labelTier ?? undefined}
            language={selectedLanguage}
            selectedVariant={
              foreignCatalog && physicalPrinting
                ? {
                    ...selectedVariant,
                    printing: {
                      ...selectedVariant.printing,
                      name: physicalPrinting,
                    },
                  }
                : selectedVariant
            }
            gradingCompanyId={selectedGradingCompanyId}
            gradeId={selectedGradeId}
            quantity={1}
            pricingEligible={
              pricingEligible &&
              (!foreignCatalog ||
                Boolean(physicalPrinting && physicalPrinting !== "Unspecified"))
            }
          />
        )}
        <div className="grid gap-2">
          <Label className="font-medium">Binder</Label>
          <AddCardToBinderBindersInput
            options={sortedBinders}
            value={selectedBinderId}
            onChange={setSelectedBinderId}
            disabled={isDisabled}
          />
        </div>

        {isCatalogError && (
          <div className="text-red-500">
            Could not load the card language. Close and try again before editing
            this card.
          </div>
        )}
        {validationErrorMessage && (
          <div className="text-red-500">{validationErrorMessage}</div>
        )}

        {!isShowAddedAnimation && (
          <Button className="w-full" disabled={isDisabled} onClick={onSubmit}>
            {isPending && <Spinner className="w-4 h-4 text-white" />}
            {languageConfirmationMode ? "Confirm language" : "Update"}
          </Button>
        )}

        {isShowAddedAnimation && (
          <Button className="w-full cursor-default" variant="success">
            Updated
          </Button>
        )}

        {wasSuccess && (
          <Button
            className="w-full cursor-default"
            variant="ghost"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            Close
          </Button>
        )}
      </div>
    </ResponsiveModal>
  );
}
